import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { ProviderComponent } from './provider/provider.component';
import {
  LeadershipApiService,
  LeadershipMember as ApiLeadershipMember,
  CatalogueApiService,
} from '@angular-workspace/services';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

const LEADERBOARD_BENCHMARK_URL = 'https://bodh.tanuh.ai/leaderboard/leaderboards';

/** Page size for the catalogue dataset list backing the leaderboard dropdown. */
const LEADERBOARD_DATASET_PAGE_SIZE = 100;

export interface LeadershipMember {
  id: number;
  rank: number;
  name: string;
  role: string;
  title: string;
  bio: string;
  image?: string;
  email?: string;
  linkedin?: string;
  expertise: string[];
  score?: number; // Optional score for competition feel
  metrics?: LeaderboardEntryMetrics;
  submittedAt?: string;
  // Additional fields from API
  publishedDatabank?: number;
  publishedAiModels?: number;
  publishedUsecases?: number;
  totalPublished?: number;
  downloads?: number;
  likes?: number;
  dislikes?: number;
  organizationId?: string;
  organizationName?: string;
  assetType?: string; // Asset type for Assets category (e.g., "Model", "Dataset", "Usecase")
  views?: number; // Number of views for assets
  members?: number; // Total number of members (for Organisation category)
}

export type DesignType = 'podium' | 'grid' | 'timeline' | 'magazine' | 'minimal';
export type LeadershipCategory = 'providers' | 'assets' | 'organisation' | 'hackathon';
/** Active leaderboard dataset track — the catalogue dataset id, fetched dynamically. */
export type HackathonTrack = string;

/** Leaderboard dataset tab, built dynamically from the catalogue (`cat/list`) response. */
export interface HackathonDatasetTab {
  /** Catalogue dataset id — also sent as `dataset_id` to the leaderboard API. */
  key: HackathonTrack;
  label: string;
}

export type LeaderboardEntryMetrics = Record<string, number>;

interface LeaderboardsResponse {
  dataset_id: string;
  /** Optional ordered metric keys returned by the benchmark API. */
  metrics?: string[];
  entries: Array<{
    rank: number;
    keycloak_sub?: string;
    name?: string;
    job_id?: string;
    dataset_id?: string;
    num_samples?: number;
    elapsed_seconds?: number;
    model_sha256?: string;
    metrics?: Partial<LeaderboardEntryMetrics> & Record<string, unknown>;
    submitted_at?: string;
  }>;
}

@Component({
  selector: 'app-leadership',
  standalone: true,
  imports: [
    CommonModule,
    AssetHeaderComponent,
    FooterComponent,
    ProviderComponent,
  ],
  templateUrl: './leadership.component.html',
  styleUrl: './leadership.component.scss',
  animations: [
    trigger('cardAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(30px) scale(0.9)' }),
        animate('600ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
      ])
    ])
  ]
})
export class LeadershipComponent implements OnInit {
  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Leaderboard' }
    ],
    title: 'Leaderboard',
    description: 'Your submission scores will appear here.'
  };

  // Active category tab
  activeCategory: LeadershipCategory = 'hackathon';
  activeHackathonTrack: HackathonTrack = '';

  // Category tabs configuration
  categoryTabs = [
    {
      key: 'hackathon' as LeadershipCategory,
      label: 'Models',
      icon: '<path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"></path>'
    }
  ];

  /** Populated dynamically from the catalogue's `adex:DataBank` datasets — see loadHackathonDatasetTabs(). */
  hackathonTabs: HackathonDatasetTab[] = [];

  get activeHackathonProblemStatementName(): string {
    return this.hackathonTabs.find((tab) => tab.key === this.activeHackathonTrack)?.label ?? '';
  }

  leadershipMembers: LeadershipMember[] = [];
  
  // Pagination state for Assets
  assetsPagination = {
    currentPage: 1,
    pageSize: 10,
    hasNext: false,
    totalCount: 0,
    isLoading: false
  };

  // Pagination state for Providers
  providersPagination = {
    currentPage: 1,
    pageSize: 10,
    hasNext: false,
    totalCount: 0,
    isLoading: false
  };

  // Pagination state for Organisation
  organisationPagination = {
    currentPage: 1,
    pageSize: 10,
    hasNext: false,
    totalCount: 0,
    isLoading: false
  };

  // Pagination-like state for hackathon view (load-more disabled)
  hackathonPagination = {
    currentPage: 1,
    pageSize: 10,
    hasNext: false,
    totalCount: 0,
    isLoading: true
  };
  hackathonLeaderboardRows: Record<string, unknown>[] = [];
  hackathonMetricColumns: string[] = [];
  private hackathonRawEntries: LeaderboardsResponse['entries'] = [];

  // Mock data (kept for reference, not used when API is available)
  mockLeadershipMembers: LeadershipMember[] = [
    {
      id: 1,
      rank: 1,
      name: 'Dr. Rajesh Kumar',
      role: 'Chief Executive Officer',
      title: 'CEO',
      bio: '',
      expertise: ['Strategic Planning', 'Digital Agriculture', 'Policy Development'],
      email: 'rajesh.kumar@tanuh.ai',
      linkedin: 'https://linkedin.com/in/rajeshkumar',
      score: 98
    },
    {
      id: 2,
      rank: 2,
      name: 'Dr. Priya Sharma',
      role: 'Chief Technology Officer',
      title: 'CTO',
      bio: 'Technology innovator specializing in AI/ML applications for agriculture. Dr. Sharma leads the development of NHA\'s cutting-edge data platform and AI model ecosystem.',
      expertise: ['AI/ML', 'Data Engineering', 'Cloud Infrastructure'],
      email: 'priya.sharma@tanuh.ai',
      linkedin: 'https://linkedin.com/in/priyasharma',
      score: 95
    },
    {
      id: 3,
      rank: 3,
      name: 'Dr. Anil Patel',
      role: 'Chief Data Officer',
      title: 'CDO',
      bio: 'Data science expert with a passion for making agricultural data accessible and actionable. Dr. Patel oversees data governance, quality, and strategic data initiatives.',
      expertise: ['Data Science', 'Data Governance', 'Analytics'],
      email: 'anil.patel@tanuh.ai',
      linkedin: 'https://linkedin.com/in/anilpatel',
      score: 92
    },
    {
      id: 4,
      rank: 4,
      name: 'Ms. Meera Desai',
      role: 'Head of Partnerships',
      title: 'Partnerships Lead',
      bio: 'Strategic partnership builder connecting farmers, researchers, and technology providers. Meera has established key collaborations that drive innovation across the agricultural ecosystem.',
      expertise: ['Partnership Development', 'Ecosystem Building', 'Stakeholder Management'],
      email: 'meera.desai@tanuh.ai',
      linkedin: 'https://linkedin.com/in/meeradesai',
      score: 89
    },
    {
      id: 5,
      rank: 5,
      name: 'Dr. Vikram Singh',
      role: 'Head of Research & Innovation',
      title: 'Research Lead',
      bio: 'Agricultural research scientist leading innovative projects that bridge the gap between research and practical farming solutions. Dr. Singh has published extensively on sustainable agriculture.',
      expertise: ['Agricultural Research', 'Innovation', 'Sustainability'],
      email: 'vikram.singh@tanuh.ai',
      linkedin: 'https://linkedin.com/in/vikramsingh',
      score: 87
    },
    {
      id: 6,
      rank: 6,
      name: 'Mr. Suresh Reddy',
      role: 'Head of Platform Operations',
      title: 'Operations Lead',
      bio: 'Operations expert ensuring seamless platform functionality and user experience. Suresh manages infrastructure, security, and platform reliability for thousands of users.',
      expertise: ['Platform Operations', 'DevOps', 'Security'],
      email: 'suresh.reddy@tanuh.ai',
      linkedin: 'https://linkedin.com/in/sureshreddy',
      score: 85
    }
  ];

  get topThree(): LeadershipMember[] {
    return this.leadershipMembers.filter(m => m.rank <= 3).sort((a, b) => a.rank - b.rank);
  }

  get others(): LeadershipMember[] {
    return this.leadershipMembers.filter(m => m.rank > 3).sort((a, b) => a.rank - b.rank);
  }

  selectedDesign: DesignType = 'magazine';

  designOptions: Array<{ value: DesignType; label: string; icon: string }> = [
    { value: 'podium', label: 'Podium', icon: '🏆' },
    { value: 'grid', label: 'Grid', icon: '📊' },
    { value: 'timeline', label: 'Timeline', icon: '📅' },
    { value: 'magazine', label: 'Magazine', icon: '📰' },
    { value: 'minimal', label: 'Minimal', icon: '✨' }
  ];

  private leadershipApiService = inject(LeadershipApiService);
  private catalogueApiService = inject(CatalogueApiService);
  private http = inject(HttpClient);

  async ngOnInit(): Promise<void> {
    await this.loadHackathonDatasetTabs();
  }

  /**
   * Fetch available leaderboard datasets from the catalogue (`adex:DataBank`) and populate
   * hackathonTabs dynamically — no hardcoded dataset list.
   *
   * Note: `cat/list` (`getFilterList`) is a filter-facet endpoint — with a `filter` array it
   * returns the distinct values across all matches (one aggregated row), not one row per
   * dataset. `cat/search` (`searchAllAssets`) is the endpoint that returns individual items
   * with `id`/`label`, so that's what backs this dropdown.
   */
  private async loadHackathonDatasetTabs(): Promise<void> {
    this.hackathonPagination.isLoading = true;
    try {
      const response = await this.catalogueApiService.searchAllAssets(
        '',
        ['Dataset'],
        [],
        1,
        LEADERBOARD_DATASET_PAGE_SIZE,
        'name:asc;itemCreatedAt:asc;',
      );
      const items = this.extractCatalogueListItems(response);
      this.hackathonTabs = items
        .filter((item) => item?.id)
        .map((item) => ({
          key: String(item.id),
          label: String(item.label || item.name || item.id),
        }));
    } catch (error) {
      console.error('Error loading leaderboard datasets:', error);
      this.hackathonTabs = [];
    }

    if (this.hackathonTabs.length > 0) {
      this.activeHackathonTrack = this.hackathonTabs[0].key;
      await this.loadHackathonLeadership(this.activeHackathonTrack, true);
    } else {
      this.activeHackathonTrack = '';
      this.clearHackathonLeaderboard();
      this.hackathonPagination.isLoading = false;
    }
  }

  /** Flattens the various catalogue response envelope shapes (`result`, `result.results`, `data`, `items`) into an item array. */
  private extractCatalogueListItems(response: any): any[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response.result)) {
      return response.result;
    }
    if (Array.isArray(response.result?.results)) {
      return response.result.results;
    }
    if (Array.isArray(response.results)) {
      return response.results;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response.items)) {
      return response.items;
    }
    return [];
  }

  /**
   * Load leadership data for providers
   * @param page Page number to load (default: 1, for load more use next page)
   */
  async loadProvidersLeadership(page: number = 1): Promise<void> {
    try {
      if (this.providersPagination.isLoading) {
        return; // Prevent multiple simultaneous requests
      }
      
      this.providersPagination.isLoading = true;
      console.log('Loading providers leadership data for page:', page);
      const response = await this.leadershipApiService.getLeadershipForProvider(page, 10);
      
      // Map API response to component's LeadershipMember format
      // API response structure: { type, title, result: [...] } or array directly
      let apiMembers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        apiMembers = response;
      } else if (response['result'] && Array.isArray(response['result'])) {
        // Response wrapped in result
        apiMembers = response['result'];
      } else if (response.members && Array.isArray(response.members)) {
        apiMembers = response.members;
      } else if (response.data && Array.isArray(response.data)) {
        apiMembers = response.data;
      }
      
      // Handle pagination info
      if (response && typeof response === 'object' && !Array.isArray(response) && 'paginationInfo' in response) {
        const paginationInfo = (response as any).paginationInfo;
        if (paginationInfo) {
          this.providersPagination.hasNext = paginationInfo.hasNext || false;
          this.providersPagination.totalCount = paginationInfo.totalCount || 0;
          this.providersPagination.currentPage = paginationInfo.page || 1;
        }
      }
      
      if (apiMembers.length > 0) {
        console.log('API members found:', apiMembers.length);
        const mappedMembers = apiMembers.map((member: any) => ({
          id: member.rank || 0, // Use rank as ID
          rank: member.rank || 0,
          name: member.username || '', // Username replaces name
          role: member.organizationName || '', // Organization name displayed below username
          title: member.organizationName || '', // Also set as title
          bio: '',
          email: member.email,
          linkedin: member.linkedin,
          expertise: [],
          score: member.totalPublished || 0, // Use totalPublished for Published metric
          // Additional fields from API
          publishedDatabank: member.publishedDatabank || member.publishedDatabanks || 0,
          publishedAiModels: member.publishedAiModels || 0,
          publishedUsecases: member.publishedUsecases || 0,
          totalPublished: member.totalPublished || 0,
          downloads: member.downloads || 0,
          likes: member.likes || 0,
          dislikes: member.dislikes || 0,
          views: member.views || 0, // Views count from API
          organizationId: member.organizationId || member.id,
          organizationName: member.organizationName,
          members: member.members || 0 // Number of members (for Provider/Organisation)
        }));
        
        if (page === 1) {
          // First page - replace the list
          this.leadershipMembers = mappedMembers;
        } else {
          // Subsequent pages - append to the list
          this.leadershipMembers = [...this.leadershipMembers, ...mappedMembers];
        }
        
        console.log('Updated leadershipMembers:', this.leadershipMembers);
      } else {
        console.log('No members found in response');
      }
      
      this.providersPagination.isLoading = false;
    } catch (error) {
      console.error('Error loading providers leadership:', error);
      this.providersPagination.isLoading = false;
    }
  }

  changeDesign(design: DesignType): void {
    this.selectedDesign = design;
  }

  async changeCategory(category: LeadershipCategory): Promise<void> {
    this.activeCategory = category;
    console.log('Category changed to:', category);
    
    // Reset pagination when switching categories
    if (category === 'assets') {
      this.assetsPagination = {
        currentPage: 1,
        pageSize: 10,
        hasNext: false,
        totalCount: 0,
        isLoading: false
      };
    } else if (category === 'providers') {
      this.providersPagination = {
        currentPage: 1,
        pageSize: 10,
        hasNext: false,
        totalCount: 0,
        isLoading: false
      };
    } else if (category === 'organisation') {
      this.organisationPagination = {
        currentPage: 1,
        pageSize: 10,
        hasNext: false,
        totalCount: 0,
        isLoading: false
      };
    } else if (category === 'hackathon') {
      // Default to the first available dataset when entering the hackathon category
      if (this.hackathonTabs.length > 0) {
        this.activeHackathonTrack = this.hackathonTabs[0].key;
      }
      this.hackathonPagination = {
        currentPage: 1,
        pageSize: 10,
        hasNext: false,
        totalCount: 0,
        isLoading: false
      };
    }
    
    // Load leadership data based on selected category
    switch (category) {
      case 'providers':
        await this.loadProvidersLeadership(1);
        break;
      case 'assets':
        await this.loadAssetsLeadership(1);
        break;
      case 'organisation':
        await this.loadOrganisationLeadership(1);
        break;
      case 'hackathon':
        await this.loadHackathonLeadership(this.activeHackathonTrack, true);
        break;
    }
  }

  changeHackathonTrack(track: HackathonTrack): void {
    if (!track || this.activeHackathonTrack === track) {
      return;
    }
    if (!this.hackathonTabs.some((tab) => tab.key === track)) {
      return;
    }
    this.activeHackathonTrack = track;
    void this.loadHackathonLeadership(track);
  }

  async loadHackathonLeadership(datasetId?: string, force = false): Promise<void> {
    try {
      if (this.hackathonPagination.isLoading && !force) {
        return;
      }

      this.hackathonPagination.isLoading = true;
      const resolvedDatasetId = datasetId ?? this.activeHackathonTrack;

      if (!resolvedDatasetId) {
        this.clearHackathonLeaderboard();
        return;
      }

      const params = new HttpParams().set('dataset_id', resolvedDatasetId);
      const response = await firstValueFrom(
        this.http.get<LeaderboardsResponse>(LEADERBOARD_BENCHMARK_URL, { params }),
      );

      this.hackathonRawEntries = Array.isArray(response?.entries) ? response.entries : [];
      this.hackathonMetricColumns = this.extractMetricColumns(response);
      this.buildHackathonTableData();
    } catch (error) {
      console.error('Error loading hackathon leadership:', error);
      this.clearHackathonLeaderboard();
    } finally {
      this.hackathonPagination.isLoading = false;
    }
  }

  private clearHackathonLeaderboard(): void {
    this.hackathonRawEntries = [];
    this.hackathonMetricColumns = [];
    this.leadershipMembers = [];
    this.hackathonLeaderboardRows = [];
    this.hackathonPagination.totalCount = 0;
    this.hackathonPagination.currentPage = 1;
    this.hackathonPagination.hasNext = false;
  }

  /**
   * Metric columns come entirely from the leaderboard API response — either the ordered
   * `metrics` list on the envelope, or (fallback) the union of keys across entry `metrics`.
   * No per-dataset hardcoded column config.
   */
  private extractMetricColumns(response: LeaderboardsResponse | null | undefined): string[] {
    if (Array.isArray(response?.metrics) && response.metrics.length > 0) {
      return response.metrics.filter((key) => key !== 'elapsed_seconds');
    }

    const keys = new Set<string>();
    for (const entry of this.hackathonRawEntries) {
      const metrics = entry.metrics;
      if (metrics && typeof metrics === 'object') {
        Object.keys(metrics).forEach((key) => {
          if (key !== 'elapsed_seconds') {
            keys.add(key);
          }
        });
      }
    }
    return Array.from(keys);
  }

  private buildHackathonTableData(): void {
    const entries = this.hackathonRawEntries;

    if (entries.length === 0) {
      this.leadershipMembers = [];
      this.hackathonLeaderboardRows = [];
      this.hackathonPagination.totalCount = 0;
      return;
    }

    const sortedEntries = this.sortHackathonEntriesByRank(entries);

    this.hackathonLeaderboardRows = sortedEntries.map((entry, index) => {
      const metrics = this.buildEntryMetrics(entry);

      return {
        rank: typeof entry.rank === 'number' ? entry.rank : index + 1,
        job_id: entry.job_id ?? '',
        name: this.getLeaderboardEntryDisplayName(entry),
        submitted_at: entry.submitted_at ?? '',
        ...metrics,
      };
    });

    this.leadershipMembers = [];
    this.hackathonPagination.totalCount = this.hackathonLeaderboardRows.length;
    this.hackathonPagination.currentPage = 1;
    this.hackathonPagination.hasNext = false;
  }

  /** Trusts the rank the leaderboard API already computed for each entry. */
  private sortHackathonEntriesByRank(
    entries: LeaderboardsResponse['entries'],
  ): LeaderboardsResponse['entries'] {
    return entries.slice().sort((a, b) => {
      const rankA = typeof a.rank === 'number' ? a.rank : Number.POSITIVE_INFINITY;
      const rankB = typeof b.rank === 'number' ? b.rank : Number.POSITIVE_INFINITY;
      if (rankA !== rankB) {
        return rankA - rankB;
      }

      const submittedDiff = this.getSubmittedAtMs(a) - this.getSubmittedAtMs(b);
      if (submittedDiff !== 0) {
        return submittedDiff;
      }

      return this.getLeaderboardEntryDisplayName(a).localeCompare(
        this.getLeaderboardEntryDisplayName(b),
      );
    });
  }

  private getLeaderboardEntryDisplayName(
    entry: LeaderboardsResponse['entries'][number],
  ): string {
    const name = typeof entry.name === 'string' ? entry.name.trim() : '';
    if (name) {
      return name;
    }
    if (entry.job_id?.trim()) {
      return entry.job_id.trim();
    }
    if (entry.model_sha256) {
      return `model:${entry.model_sha256.slice(0, 12)}…`;
    }
    return 'Submission';
  }

  private getSubmittedAtMs(entry: LeaderboardsResponse['entries'][number]): number {
    if (!entry.submitted_at) {
      return Number.POSITIVE_INFINITY;
    }
    const parsed = Date.parse(entry.submitted_at);
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  }

  private coerceMetricDisplayValue(value: unknown): number {
    if (value === null || value === undefined) {
      return 0;
    }
    if (typeof value === 'number' && !Number.isNaN(value) && Number.isFinite(value)) {
      return value;
    }
    return 0;
  }

  private buildEntryMetrics(
    entry: LeaderboardsResponse['entries'][number],
  ): LeaderboardEntryMetrics {
    const metrics = this.normalizeMetrics(entry.metrics);
    if (typeof entry.elapsed_seconds === 'number' && !Number.isNaN(entry.elapsed_seconds)) {
      metrics['elapsed_seconds'] = entry.elapsed_seconds;
    }
    return metrics;
  }

  private normalizeMetrics(metrics: unknown): LeaderboardEntryMetrics {
    const source = metrics && typeof metrics === 'object' ? (metrics as Record<string, unknown>) : {};
    const normalized: LeaderboardEntryMetrics = {};

    for (const [key, value] of Object.entries(source)) {
      normalized[key] = this.coerceMetricDisplayValue(value);
    }

    return normalized;
  }

  downloadActiveHackathonCsv(): void {
    const rows = this.hackathonLeaderboardRows;
    if (!rows || rows.length === 0) {
      return;
    }

    const headers = Array.from(
      rows.reduce((set: Set<string>, row: any) => {
        Object.keys(row || {}).forEach((key) => set.add(key));
        return set;
      }, new Set<string>())
    );

    const escapeCsv = (value: unknown): string => {
      if (value === null || value === undefined) {
        return '';
      }
      const serialized =
        typeof value === 'object'
          ? JSON.stringify(value)
          : String(value);
      return /[",\n]/.test(serialized)
        ? `"${serialized.replace(/"/g, '""')}"`
        : serialized;
    };

    const csvLines = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row: any) => headers.map((header) => escapeCsv(row?.[header])).join(',')),
    ];

    const selectedTrack = this.hackathonTabs.find((tab) => tab.key === this.activeHackathonTrack);
    const csvContent = '\uFEFF' + csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `tanuh-hackathon-${selectedTrack?.label?.toLowerCase().replace(/\s+/g, '-') || 'track'}-leaderboard.csv`;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Load leadership data for assets
   * @param page Page number to load (default: 1, for load more use next page)
   */
  async loadAssetsLeadership(page: number = 1): Promise<void> {
    try {
      if (this.assetsPagination.isLoading) {
        return; // Prevent multiple simultaneous requests
      }
      
      // Set loading state BEFORE the API call
      this.assetsPagination.isLoading = true;
      console.log('Loading assets leadership data for page:', page);
      
      const response = await this.leadershipApiService.getLeaderboardForAssets(page, 10);
      
      // Map API response to component's LeadershipMember format
      let apiMembers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        apiMembers = response;
      } else if (response['result'] && Array.isArray(response['result'])) {
        // Response wrapped in result
        apiMembers = response['result'];
      } else if (response.members && Array.isArray(response.members)) {
        apiMembers = response.members;
      } else if (response.data && Array.isArray(response.data)) {
        apiMembers = response.data;
      }
      
      // Handle pagination info
      if (response && typeof response === 'object' && !Array.isArray(response) && 'paginationInfo' in response) {
        const paginationInfo = (response as any).paginationInfo;
        if (paginationInfo) {
          this.assetsPagination.hasNext = paginationInfo.hasNext || false;
          this.assetsPagination.totalCount = paginationInfo.totalCount || 0;
          this.assetsPagination.currentPage = paginationInfo.page || 1;
        }
      }
      
      if (apiMembers.length > 0) {
        console.log('API members found:', apiMembers.length);
        const mappedMembers = apiMembers.map((member: any) => ({
          id: member.id || member.rank || 0,
          rank: member.rank || 0,
          name: member.name || member.organizationName || '', // Asset name from API
          role: member.organizationName || '', // Organization name displayed below asset name
          title: member.organizationName || '',
          bio: member.shortDescription || '',
          email: member.email,
          linkedin: member.linkedin,
          expertise: [],
          score: member.totalPublished || 0,
          publishedDatabank: member.publishedDatabank || member.publishedDatabanks || 0,
          publishedAiModels: member.publishedAiModels || 0,
          publishedUsecases: member.publishedUsecases || 0,
          totalPublished: member.totalPublished || 0,
          downloads: member.downloads || 0,
          likes: member.likes || 0,
          dislikes: member.dislikes || 0,
          views: member.views || 0, // Views count from API
          organizationId: member.organizationId || member.id,
          organizationName: member.organizationName,
          assetType: this.formatAssetType(member.type) // Format asset type from API (e.g., "DATABANK", "AI_MODEL")
        }));
        
        if (this.assetsPagination.currentPage === 1) {
          // First page - replace the list
          this.leadershipMembers = mappedMembers;
        } else {
          // Subsequent pages - append to the list
          this.leadershipMembers = [...this.leadershipMembers, ...mappedMembers];
        }
        
        console.log('Updated leadershipMembers:', this.leadershipMembers);
      } else {
        console.log('No members found in response');
      }
      
      this.assetsPagination.isLoading = false;
    } catch (error) {
      console.error('Error loading assets leadership:', error);
      this.assetsPagination.isLoading = false;
    }
  }

  /**
   * Load more assets (next page)
   */
  async loadMoreAssets(): Promise<void> {
    if (this.assetsPagination.hasNext && !this.assetsPagination.isLoading) {
      const nextPage = this.assetsPagination.currentPage + 1;
      await this.loadAssetsLeadership(nextPage);
    }
  }

  /**
   * Load more providers (next page)
   */
  async loadMoreProviders(): Promise<void> {
    if (this.providersPagination.hasNext && !this.providersPagination.isLoading) {
      const nextPage = this.providersPagination.currentPage + 1;
      await this.loadProvidersLeadership(nextPage);
    }
  }

  /**
   * Load more organisations (next page)
   */
  async loadMoreOrganisations(): Promise<void> {
    if (this.organisationPagination.hasNext && !this.organisationPagination.isLoading) {
      const nextPage = this.organisationPagination.currentPage + 1;
      await this.loadOrganisationLeadership(nextPage);
    }
  }

  /**
   * Load leadership data for organisation
   * @param page Page number to load (default: 1, for load more use next page)
   */
  async loadOrganisationLeadership(page: number = 1): Promise<void> {
    try {
      if (this.organisationPagination.isLoading) {
        return; // Prevent multiple simultaneous requests
      }
      
      this.organisationPagination.isLoading = true;
      console.log('Loading organisation leadership data for page:', page);
      const response = await this.leadershipApiService.getLeaderboardForOrganisations(page, 10);
      
      // Map API response to component's LeadershipMember format
      // Organisation API returns array directly or wrapped in result
      let apiMembers: any[] = [];
      
      if (Array.isArray(response)) {
        // Response is directly an array
        apiMembers = response;
      } else if (response['result'] && Array.isArray(response['result'])) {
        // Response wrapped in result
        apiMembers = response['result'];
      } else if (response.members && Array.isArray(response.members)) {
        apiMembers = response.members;
      } else if (response.data && Array.isArray(response.data)) {
        apiMembers = response.data;
      }
      
      // Handle pagination info
      if (response && typeof response === 'object' && !Array.isArray(response) && 'paginationInfo' in response) {
        const paginationInfo = (response as any).paginationInfo;
        if (paginationInfo) {
          this.organisationPagination.hasNext = paginationInfo.hasNext || false;
          this.organisationPagination.totalCount = paginationInfo.totalCount || 0;
          this.organisationPagination.currentPage = paginationInfo.page || 1;
        }
      }
      
      if (apiMembers.length > 0) {
        console.log('API members found:', apiMembers.length);
        const mappedMembers = apiMembers.map((member: any) => ({
          id: member.rank || 0,
          rank: member.rank || 0,
          name: member.organizationName || '', // Organisation uses organizationName as name
          role: member.organizationName || '', // Display organization name below username
          title: member.organizationName || '',
          bio: '',
          email: member.email,
          linkedin: member.linkedin,
          expertise: [],
          score: member.totalPublished || 0, // Use totalPublished for Published metric
          publishedDatabank: member.publishedDatabanks || member.publishedDatabank || 0, // Note: API uses publishedDatabanks (plural)
          publishedAiModels: member.publishedAiModels || 0,
          publishedUsecases: member.publishedUsecases || 0,
          totalPublished: member.totalPublished || 0,
          downloads: member.downloads || 0,
          likes: member.likes || 0,
          dislikes: member.dislikes || 0,
          views: member.views || 0, // Views count from API
          organizationId: member.id, // Organisation API uses 'id' for organizationId
          organizationName: member.organizationName,
          members: member.members || 0 // Number of members in the organization
        }));
        
        if (page === 1) {
          // First page - replace the list
          this.leadershipMembers = mappedMembers;
        } else {
          // Subsequent pages - append to the list
          this.leadershipMembers = [...this.leadershipMembers, ...mappedMembers];
        }
        
        console.log('Updated leadershipMembers:', this.leadershipMembers);
      } else {
        console.log('No members found in response');
      }
      
      this.organisationPagination.isLoading = false;
    } catch (error) {
      console.error('Error loading organisation leadership:', error);
      this.organisationPagination.isLoading = false;
    }
  }

  /**
   * Format asset type from API format to display format
   * @param assetType Raw asset type from API
   * @returns Formatted asset type (e.g., "Model", "Dataset", "Usecase")
   */
  private formatAssetType(assetType: string | string[] | undefined): string {
    if (!assetType) return 'N/A';
    
    // Handle array of types (take first one)
    let typeStr = '';
    if (Array.isArray(assetType) && assetType.length > 0) {
      typeStr = assetType[0];
    } else if (typeof assetType === 'string') {
      typeStr = assetType;
    } else {
      return 'N/A';
    }
    
    // Extract type name after colon (e.g., "adex:Apps" -> "Apps")
    const parts = typeStr.split(':');
    let typeName = parts.length > 1 ? parts[parts.length - 1] : typeStr;
    
    // Normalize case for comparison
    const normalized = typeName.toLowerCase().replace(/[\s_-]/g, '');
    
    // Map to display format
    if (normalized === 'aimodel' || normalized === 'ai_model' || normalized === 'model') {
      return 'Model';
    } else if (normalized === 'databank' || normalized === 'data_bank' || normalized === 'dataset') {
      return 'Dataset';
    } else if (normalized === 'apps' || normalized === 'app' || normalized === 'usecase' || normalized === 'use_case') {
      return 'Usecase';
    }
    
    // Handle uppercase API formats (e.g., "DATABANK", "AI_MODEL")
    const upperNormalized = typeName.toUpperCase();
    if (upperNormalized === 'DATABANK' || upperNormalized === 'DATA_BANK') {
      return 'Dataset';
    } else if (upperNormalized === 'AI_MODEL' || upperNormalized === 'AIMODEL') {
      return 'Model';
    } else if (upperNormalized === 'APPS' || upperNormalized === 'USECASE' || upperNormalized === 'USE_CASE') {
      return 'Usecase';
    }
    
    // Fallback: capitalize first letter
    return typeName.charAt(0).toUpperCase() + typeName.slice(1).toLowerCase();
  }
}
